<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { formatDateWithTime } from '@/utils/date/date-time'
import { useField } from 'vee-validate'

const props = defineProps<{
  alreadySelectedPeriod?: Date | Date[] | {}
  placeholder: string
  name: string
}>()

const emits = defineEmits<{
  (e: 'change-date', period: Date[]): void
}>()

const { value: inputValue, errorMessage } = useField<Date[]>(props.name)

const refStartDate = ref<Date | null>(null)
const refEndDate = ref<Date | null>(null)

const showModal = ref(false)
const errorMsg = ref('')

const now = new Date()
const defaultStartDateTime = new Date(now.getTime() - 24 * 60 * 60 * 1000)

function formatDateForInput(date: Date): string {
  return date.toLocaleDateString('sv-SE')
}

function formatTimeForInput(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
}

const modalStartDate = ref(formatDateForInput(defaultStartDateTime))
const modalStartTime = ref(formatTimeForInput(defaultStartDateTime))
const modalEndDate = ref(formatDateForInput(now))
const modalEndTime = ref(formatTimeForInput(now))

function populateDatePicker() {
  if (Array.isArray(props.alreadySelectedPeriod) && props.alreadySelectedPeriod.length) {
    const [start, end] = props.alreadySelectedPeriod as any[]
    const startDateObj = start instanceof Date ? start : new Date(start)
    const endDateObj = end instanceof Date ? end : new Date(end)

    refStartDate.value = startDateObj
    refEndDate.value = endDateObj
    inputValue.value = [startDateObj, endDateObj]
    modalStartDate.value = formatDateForInput(startDateObj)
    modalStartTime.value = formatTimeForInput(startDateObj)
    modalEndDate.value = formatDateForInput(endDateObj)
    modalEndTime.value = formatTimeForInput(endDateObj)
  }
}

function openModal() {
  showModal.value = true
  errorMsg.value = ''
}

function cancelSelection() {
  showModal.value = false
  errorMsg.value = ''
  modalStartDate.value = refStartDate.value ? formatDateForInput(refStartDate.value) : formatDateForInput(defaultStartDateTime)
  modalStartTime.value = refStartDate.value ? formatTimeForInput(refStartDate.value) : formatTimeForInput(defaultStartDateTime)
  modalEndDate.value = refEndDate.value ? formatDateForInput(refEndDate.value) : formatDateForInput(now)
  modalEndTime.value = refEndDate.value ? formatTimeForInput(refEndDate.value) : formatTimeForInput(now)
}

function confirmSelection() {
  const start = new Date(`${modalStartDate.value}T${modalStartTime.value}`)
  const end = new Date(`${modalEndDate.value}T${modalEndTime.value}`)

  if (end <= start) {
    errorMsg.value = 'A data/hora de fim deve ser posterior à data/hora de início.'
    return
  }

  refStartDate.value = start
  refEndDate.value = end
  inputValue.value = [start, end]
  emits('change-date', [start, end])
  showModal.value = false
}

const computedPlaceholder = computed(() => {
  if (refStartDate.value && refEndDate.value) {
    return `${formatDateWithTime(refStartDate.value)} até ${formatDateWithTime(refEndDate.value)}`
  }
  return props.placeholder
})

onMounted(() => {
  populateDatePicker()
})
</script>

<template>
  <div>
    <input
      class="datepicker text-gray w-full border border-zinc-300 px-5 py-3 font-normal outline-none transition focus:border-primary"
      :placeholder="computedPlaceholder"
      @click="openModal"
      readonly />

    <transition name="fade">
      <div v-if="showModal" class="modal-overlay" @click.self="cancelSelection">
        <div class="modal-content">
          <h2 class="mb-4 text-2xl font-semibold">Selecione o período</h2>
          <div class="flex flex-col gap-4">
            <div>
              <label class="mb-1 block font-medium">Data de Início</label>
              <input type="date" v-model="modalStartDate" class="w-full border px-2 py-3" />
              <label class="mb-1 mt-2 block font-medium">Hora de Início</label>
              <input type="time" v-model="modalStartTime" class="w-full border px-2 py-3" />
            </div>

            <div>
              <label class="mb-1 mt-4 block font-medium">Data de Fim</label>
              <input type="date" v-model="modalEndDate" class="w-full border px-2 py-3" />
              <label class="mb-1 mt-2 block font-medium">Hora de Fim</label>
              <input type="time" v-model="modalEndTime" class="w-full border px-2 py-3" />
            </div>

            <div v-if="errorMsg" class="text-sm text-red-500">
              {{ errorMsg }}
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button type="button" @click="cancelSelection" class="bg-gray-300 px-4 py-3 text-gray-800 transition hover:bg-gray-400">Cancelar</button>
            <button type="button" @click="confirmSelection" class="hover:bg-primary-dark bg-primary px-4 py-3 text-white transition">Confirmar</button>
          </div>
        </div>
      </div>
    </transition>

    <p v-if="errorMessage" class="absolute text-title-xsm4 font-medium text-red">
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 0;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
</style>
